enum Role {
  Cardholder = "Cardholder",
  Contractor = "Contractor",
  Employee = "Employee",
  Approver = "Approver",
  Integration = "Integration",
  Manager = "Manager",
  Auditor = "Auditor",
  Accountant = "Accountant",
  Admin = "Admin",
  SuperAdmin = "SuperAdmin",
  Owner = "Owner",
  PlatformOwner = "PlatformOwner",
  PlatformAdmin = "PlatformAdmin",
}

/**
 * Rank of each role. LOWER number = HIGHER authority.
 *
 * Source of truth: backend USER_ROLE_RANK in
 * shared/src/dModels/utils/Constants.js. Keep these two in sync.
 *
 * Authority rule: a user may only assign or edit roles whose rank is the same
 * as or lower than their own (i.e. ROLE_RANK[target] >= ROLE_RANK[actor]).
 */
export const ROLE_RANK: Record<Role, number> = {
  [Role.SuperAdmin]: 0,
  [Role.PlatformOwner]: 1,
  [Role.PlatformAdmin]: 2,
  [Role.Owner]: 3,
  [Role.Admin]: 4,
  [Role.Accountant]: 5,
  [Role.Auditor]: 6,
  [Role.Manager]: 7,
  // Same level
  [Role.Employee]: 8,
  [Role.Contractor]: 8,
  [Role.Integration]: 8,
  [Role.Approver]: 8,
  [Role.Cardholder]: 9,
};

export const getRoleRank = (role: Role): number => ROLE_RANK[role];

/**
 * True if `actor` may act on (assign or edit) something at `target` authority.
 * Same or higher authority wins; equal rank is allowed.
 */
export const canActOnRole = (actor: Role, target: Role): boolean =>
  ROLE_RANK[actor] <= ROLE_RANK[target];

/** Roles that live in /my alone, with no admin pages. Approver and Integration keep theirs. */
export const MY_ONLY_ROLES: readonly Role[] = [
  Role.Employee,
  Role.Contractor,
  Role.Cardholder,
];

/** A /my-only role, or no known role at all. */
export const isMyOnlyRole = (role?: string | null): boolean =>
  !role ||
  !Object.prototype.hasOwnProperty.call(ROLE_RANK, role) ||
  MY_ONLY_ROLES.includes(role as Role);

/**
 * Platform-level roles. These carry authority across every company on the platform, so
 * granting one is company-scoped: see isPlatformRole in the backend Authorization service.
 */
export const isPlatformRole = (role: Role): boolean =>
  role === Role.PlatformAdmin || role === Role.PlatformOwner;

export default Role;
